import React, { useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { Controller, SubmitHandler, UseFormReturn } from 'react-hook-form';
import { ITEM_HEIGHT, ITEM_PADDING_TOP } from 'entities/distribution/model/consts';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { useGetUserRolesQuery } from 'features/getUserRoles/getUserRolesApi';
import { useGetSourcesQuery } from 'features/getSources/api/getSourcesApi';
import { useGetCountriesQuery } from 'features/getCountries/api/getCountriesApi';
import { DataUserMPFormType } from 'entities/users/types';
import { CircularLoader } from 'shared/ui/CircularLoader/CircularLoader';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useCheckB2BIINInformationMutation } from 'features/getB2BConstants/b2bConstants';
import { CountrySelectId } from 'shared/consts';

/*
@example
{
    "surname": "МАКАРОВ",
    "name": "АЛЕКСАНДР",
    "middle_name": "АЛЕКСЕЕВИЧ",
    "birthdate": "19-11-1982",
    "gender": "male",
    "iin": "821119301118"
}
 */

type UsersMPCardType = {
  title: string;
  form: UseFormReturn<DataUserMPFormType>;
  textButton: string;
  onSubmit: SubmitHandler<DataUserMPFormType>;
};

export const UsersCard = ({ title, form, textButton, onSubmit }: UsersMPCardType) => {
  const dispatch = useDispatch();

  const { data: citiesList, isSuccess: citiesListSuccess } = useGetCitiesQuery({ sort: 'country_id,name_ru' });
  const { data: roles, isSuccess: rolesListSuccess } = useGetUserRolesQuery(null);
  const { data: sources, isSuccess: sourcesListSuccess } = useGetSourcesQuery(null);
  const { data: countriesList, isSuccess: countriesListSuccess } = useGetCountriesQuery(null);
  const [checkB2BIINInformationMutation, { data: b2bIINInformation }] = useCheckB2BIINInformationMutation();

  const { control, handleSubmit, watch, setValue, formState: { errors = {} } = {} } = form;

  const selectedCountryWatch = watch('selectedCountryCode', '');
  const countryIdWatch = watch('country_id');
  const iinWatch = watch('iin');
  const cityIdWatch = watch('city_id');

  const selectCity = citiesList?.find((city) => city.id === cityIdWatch);

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  useEffect(() => {
    if (iinWatch && iinWatch.length === 12 && watch().country_id === 1) checkB2BIINInformationMutation(iinWatch);
  }, [iinWatch]);

  useEffect(() => {
    if (b2bIINInformation?.name) setValue('first_name', b2bIINInformation.name);
    if (b2bIINInformation?.surname) setValue('last_name', b2bIINInformation.surname);
    if (b2bIINInformation?.middle_name) setValue('middle_name', b2bIINInformation.middle_name);
  }, [b2bIINInformation]);

  if (!(rolesListSuccess && citiesListSuccess && sourcesListSuccess && countriesListSuccess)) {
    return <CircularLoader />;
  }

  const handleCountryCodeChange = () => {
    setValue('phone', '');
  };

  const handleDateChange = (value: Dayjs | null, onChange: (...event: any[]) => void) => {
    onChange(value ? dayjs(value).format('YYYY-MM-DD') : null);
  };

  return (
    <div>
      <div className="header">
        <Typography
          variant="h5"
          sx={{ fontWeight: '600' }}
        >
          {title}
        </Typography>
        <IconButton
          onClick={() => dispatch(setDrawerOpen(false))}
          sx={{ color: '#000' }}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className="content">
        <Controller
          control={control}
          name="first_name"
          render={({ field, fieldState }) => (
            <TextField
              label="Имя"
              fullWidth
              sx={{ marginBottom: '24px' }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="last_name"
          render={({ field, fieldState }) => (
            <TextField
              label="Фамилия"
              fullWidth
              sx={{ marginBottom: '24px' }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="middle_name"
          render={({ field, fieldState }) => (
            <TextField
              label="Отчество"
              fullWidth
              sx={{ marginBottom: '24px' }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Box>
          <Box sx={{ display: 'flex' }}>
            <Controller
              control={control}
              name="selectedCountryCode"
              render={({ field }) => (
                <FormControl
                  sx={{ minWidth: 100 }}
                  error={!!errors.selectedCountryCode}
                >
                  <InputLabel>Код</InputLabel>
                  <Select
                    label="Код"
                    {...field}
                    onChange={(event) => {
                      const countryCode = event.target.value as string;
                      field.onChange(countryCode);
                      handleCountryCodeChange();
                    }}
                  >
                    <MenuItem value="+7">+7</MenuItem>
                    <MenuItem value="+996">+996</MenuItem>
                    <MenuItem value="+998">+998</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="phone"
              render={({ field, fieldState }) => (
                <TextField
                  sx={{ marginBottom: '24px' }}
                  label="Номер телефона"
                  fullWidth
                  disabled={selectedCountryWatch.length < 1}
                  error={!!fieldState.error}
                  inputProps={{
                    maxLength: selectedCountryWatch === '+7' ? 10 : 9,
                  }}
                  {...field}
                />
              )}
            />
          </Box>
          {errors.selectedCountryCode?.message || errors.phone?.message ? (
            <FormHelperText
              sx={{ marginLeft: '14px' }}
              error
            >
              {errors.selectedCountryCode?.message || errors.phone?.message}
            </FormHelperText>
          ) : null}
        </Box>

        <Controller
          control={control}
          name="role"
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel id="select-label-type">Роль</InputLabel>
              <Select
                labelId="select-label-type"
                label="Роль"
                MenuProps={MenuProps}
                sx={{ marginBottom: '24px' }}
                {...field}
              >
                {roles?.map((role) => (
                  <MenuItem
                    key={role.id}
                    value={role.id}
                  >
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="source_id"
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel id="select-label-type">Источник</InputLabel>
              <Select
                labelId="select-label-type"
                label="Источник"
                MenuProps={MenuProps}
                sx={{ marginBottom: '24px' }}
                {...field}
              >
                {sources?.map((source) => (
                  <MenuItem
                    key={source.id}
                    value={source.id}
                  >
                    {source.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="city_id"
          render={({ field: { onChange } }) => (
            <FormControl
              fullWidth
              error={!!errors.city_id}
              sx={{ marginBottom: '24px' }}
            >
              <Autocomplete
                disablePortal
                disabled={!watch().country_id}
                options={citiesList.filter((city) => city.country_id === countryIdWatch) || []}
                value={selectCity || null}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(options) => options?.name_ru || options.name}
                loading={citiesListSuccess}
                loadingText="По вашему запросу ничего не найдено"
                onChange={(event, value) => onChange(value?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Город"
                  />
                )}
              />
              {errors.city_id?.message && <FormHelperText>{errors.city_id.message.toString()}</FormHelperText>}
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="country_id"
          render={({ field }) => (
            <FormControl
              fullWidth
              error={!!errors.country_id}
              sx={{ marginBottom: '24px' }}
            >
              <InputLabel id="select-label-type">Страна</InputLabel>
              <Select
                labelId="select-label-type"
                label="Страна"
                MenuProps={MenuProps}
                {...field}
              >
                {countriesList?.map((country) => (
                  <MenuItem
                    key={country.id}
                    value={country.id}
                  >
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.country_id?.message && <FormHelperText>{errors.country_id.message.toString()}</FormHelperText>}
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="iin"
          render={({ field }) => (
            <TextField
              label={countryIdWatch === CountrySelectId.UZ || countryIdWatch === CountrySelectId.KG ? 'ИНН' : 'ИИН'}
              fullWidth
              sx={{ marginBottom: '24px' }}
              {...field}
            />
          )}
        />

        {countryIdWatch === CountrySelectId.UZ || countryIdWatch === CountrySelectId.KG ? (
          <>
            <Controller
              control={control}
              name="document_number"
              render={({ field, fieldState }) => (
                <TextField
                  label="Номер паспорта"
                  fullWidth
                  sx={{ marginBottom: '24px' }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  {...field}
                />
              )}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box display="flex" />
              <Controller
                control={control}
                name="issued_at"
                render={({ field: { onChange, value, ...restField }, fieldState }) => (
                  <FormControl
                    error={!!fieldState.error}
                    fullWidth
                    sx={{ marginBottom: '24px' }}
                  >
                    <DatePicker
                      sx={{ width: '60%' }}
                      label="Дата выдачи паспорта"
                      value={value ? dayjs(value) : null}
                      format="DD/MM/YYYY"
                      onChange={(value) => handleDateChange(value, onChange)}
                      {...restField}
                    />
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="expiration_at"
                render={({ field: { onChange, value, ...restField }, fieldState }) => (
                  <FormControl
                    error={!!fieldState.error}
                    fullWidth
                    sx={{ marginBottom: '24px' }}
                  >
                    <DatePicker
                      sx={{ width: '60%' }}
                      label="Дата истечения срока паспорта"
                      value={value ? dayjs(value) : null}
                      format="DD/MM/YYYY"
                      onChange={(value) => handleDateChange(value, onChange)}
                      {...restField}
                    />
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="personal_number"
                render={({ field, fieldState }) => (
                  <TextField
                    sx={{ marginBottom: '24px', width: '60%' }}
                    label="Персональный номер"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    {...field}
                  />
                )}
              />
            </LocalizationProvider>
          </>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            marginTop: '30px',
          }}
        >
          <Button
            onClick={handleSubmit?.(onSubmit)}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            {textButton}
          </Button>
        </Box>
      </div>
    </div>
  );
};
